import type { TemperatureMode } from './types';

/**
 * Doména přehledu tras (role MASTER).
 *
 * Rozdíl proti `api/types.ts` platí i tady: tohle je tvar, se kterým pracuje UI.
 * Mapování z DTO je v `api/mapRoutes.ts`.
 *
 * Klíčová definice, na které celý přehled stojí — **co je nedokončená zásilka**:
 *
 *   - `INCOMPLETE` — není kompletně naskenovaná ve skladu a není naložená
 *   - `NOT_LOADED` — naskenovaná je celá, ale není naložená
 *   - `LOADED`     — všechny balíky naložené, hotovo
 *
 * Nedokončená = `INCOMPLETE` nebo `NOT_LOADED`. Rozlišují se schválně:
 * chybějící sken znamená „balík ve skladu možná vůbec není“, kdežto
 * nenaložený balík ve skladu prokazatelně leží. Pro mistra jsou to dva různé
 * problémy a každý se řeší jinak.
 */
export type ShipmentProgress = 'LOADED' | 'NOT_LOADED' | 'INCOMPLETE' | 'UNKNOWN';

export interface RouteParcelInfo {
  packageNumber: string;
  temperature: TemperatureMode | null;
  /** Kdy byl balík naložen na vůz. `null` = nenaloženo. */
  loadedAt: string | null;
  /** Kdy byl balík načten skladem. `null` = nenaskenováno. */
  whScannedAt: string | null;
}

export interface RouteShipmentInfo {
  shipmentId: string;
  shipmentNumber: string;
  sequence: number | null;
  recipientName: string | null;
  address: string | null;
  sender: string | null;
  weightKg: number | null;

  /** Prázdné pole = backend rozpad na balíky neposlal, stav se pak neodvozuje. */
  parcels: RouteParcelInfo[];
  parcelCount: number;
  scannedCount: number;
  loadedCount: number;

  progress: ShipmentProgress;
}

export interface RouteSummary {
  routeNo: string;
  driverName: string | null;
  vehicle: string | null;
  plate: string | null;
  shipments: number;
  parcels: number;
  loadedParcels: number;
  /**
   * Balíky, které někdo prokazatelně viděl — naskenované ve skladu **nebo**
   * naložené.
   *
   * Backend posílá obě čísla zvlášť a nesjednocuje je, takže přesný průnik
   * neznáme; `Math.max` je dolní mez. Pro rozhodnutí „jdi naložit“ vs.
   * „jdi hledat“ to stačí a nikdy nenadhodnotí — což je ta bezpečná strana:
   * radši pošle mistra hledat balík, který se najde, než aby ho neposlal.
   */
  scannedParcels: number;
  /** Kolik balíků trasy ještě není naložených. */
  remainingParcels: number;
  /** Balíky, které nikdo neviděl — ani sken, ani naložení. */
  unseenParcels: number;
  /** Trasa přihlášeného uživatele — v seznamu se zvýrazní. */
  mine: boolean;
  complete: boolean;
}

export interface RouteDetail {
  routeNo: string;
  driverName: string | null;
  vehicle: string | null;
  plate: string | null;
  shipments: RouteShipmentInfo[];
  /** Součty přes zásilky trasy — hlavička detailu je nedopočítává znovu. */
  totals: {
    shipments: number;
    parcels: number;
    scanned: number;
    loaded: number;
    /** Zásilky ve stavu `INCOMPLETE` nebo `NOT_LOADED`. */
    unfinished: number;
  };
}

/** Nedokončená zásilka i s číslem trasy — přehled jde napříč trasami. */
export interface OverviewShipment extends RouteShipmentInfo {
  routeNo: string | null;
}

/** Zásilka, kterou musí mistr ještě řešit. */
export function isUnfinished(shipment: RouteShipmentInfo): boolean {
  return shipment.progress === 'INCOMPLETE' || shipment.progress === 'NOT_LOADED';
}

/**
 * „Rozskenovaná“ zásilka — část balíků naskenovaná, část ne.
 *
 * Užší než `INCOMPLETE` schválně. `INCOMPLETE` zahrnuje i zásilky, kterých se
 * ještě nikdo nedotkl (0 z 3) — ty ale nikdo „nerozskenoval“ a v hale se řeší
 * jinak: nezačatá zásilka čeká na svoje pořadí, rozskenovaná znamená, že
 * někdo objednávku rozdělal a odešel od ní. Právě ta se ztrácí.
 *
 * Bez rozpadu na balíky (`UNKNOWN`) se nic neodvozuje — viz `resolveProgress`.
 */
export function isPartiallyScanned(shipment: RouteShipmentInfo): boolean {
  if (shipment.progress !== 'INCOMPLETE') return false;
  return shipment.scannedCount > 0 && shipment.scannedCount < shipment.parcelCount;
}

const ORDER: Record<ShipmentProgress, number> = {
  INCOMPLETE: 0,
  NOT_LOADED: 1,
  UNKNOWN: 2,
  LOADED: 3,
};

/**
 * Nedokončené nahoru, hotové dolů; uvnitř skupiny podle pořadí na trase.
 *
 * Mistr otevírá detail proto, aby našel problém — ne aby scrolloval sto
 * hotových zásilek. Pořadí uvnitř skupiny zůstává podle `seq`, aby se v hale
 * dalo jít regálem popořadě.
 */
export function sortForMaster(shipments: RouteShipmentInfo[]): RouteShipmentInfo[] {
  return [...shipments].sort((a, b) => {
    const byProgress = ORDER[a.progress] - ORDER[b.progress];
    if (byProgress !== 0) return byProgress;
    if (a.sequence !== null && b.sequence !== null) return a.sequence - b.sequence;
    if (a.sequence !== null) return -1;
    if (b.sequence !== null) return 1;
    return a.shipmentNumber.localeCompare(b.shipmentNumber);
  });
}

/**
 * Stav zásilky z balíků.
 *
 * Bez rozpadu na balíky se stav neodvozuje — `UNKNOWN` je bezpečnější než
 * dopočet z holých počítadel, který by při nesouladu tiše ukázal „hotovo“.
 */
export function resolveProgress(parcels: RouteParcelInfo[]): ShipmentProgress {
  if (parcels.length === 0) return 'UNKNOWN';

  const loaded = parcels.filter((p) => p.loadedAt !== null).length;
  if (loaded === parcels.length) return 'LOADED';

  // Naložený balík je nutně i naskenovaný, i kdyby zápis skenu chyběl —
  // fyzicky prošel rukama. Jinak by naložená trasa svítila jako nenaskenovaná.
  const scanned = parcels.filter((p) => p.whScannedAt !== null || p.loadedAt !== null).length;
  return scanned === parcels.length ? 'NOT_LOADED' : 'INCOMPLETE';
}
